import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isAltDev, isLocalDev } from '../../themeEnvironment';
import {
  getRunningClientFingerprint,
  loadClientBuildInfo,
} from './clientBuildInfo';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

type VersionResponse = {
  client?: {
    fingerprint?: string | null;
  };
  catalog?: {
    coursesLatestPublishedAt?: string | null;
    gamesLatestImportedAt?: string | null;
  };
};

type CatalogSeen = {
  courses: string | null;
  games: string | null;
};

export type VersionCheckHandlers = {
  onClientMismatch: () => void;
  onClientMatch?: () => void;
  onCatalogChange?: (detail: { courses: boolean; games: boolean }) => void;
};

const shouldSkipVersionCheck = (): boolean =>
  isAltDev || isLocalDev || process.env.NODE_ENV === 'development';

function isCatalogTimestampNewer(
  next: string | null,
  previous: string | null,
): boolean {
  if (!next) {
    return false;
  }

  if (!previous) {
    return true;
  }

  return next > previous;
}

export function useVersionCheck(handlers: VersionCheckHandlers): void {
  const location = useLocation();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const localFingerprintRef = useRef<string | null>(null);
  const catalogSeenRef = useRef<CatalogSeen | null>(null);
  const checkingRef = useRef(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (shouldSkipVersionCheck()) {
      return;
    }

    const runningFingerprint = getRunningClientFingerprint();
    if (runningFingerprint) {
      localFingerprintRef.current = runningFingerprint;
      setEnabled(true);
      return;
    }

    let cancelled = false;

    void loadClientBuildInfo().then((info) => {
      if (cancelled || !info?.fingerprint) {
        return;
      }

      localFingerprintRef.current = info.fingerprint;
      setEnabled(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const check = useCallback(async () => {
    if (!enabled || checkingRef.current || !localFingerprintRef.current) {
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      return;
    }

    const runningFingerprint = getRunningClientFingerprint();
    if (runningFingerprint) {
      localFingerprintRef.current = runningFingerprint;
    }

    checkingRef.current = true;
    try {
      const fetchNoStore = (url: string) =>
        fetch(url, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        });

      const [versionResponse, deployedBuildResponse] = await Promise.all([
        fetchNoStore(`${apiUrl}/version`),
        fetchNoStore('/client-build.json'),
      ]);

      if (!versionResponse.ok) {
        return;
      }

      const data = (await versionResponse.json()) as VersionResponse;

      let deployedFingerprint: string | null = null;
      if (deployedBuildResponse.ok) {
        const buildInfo = (await deployedBuildResponse.json()) as {
          fingerprint?: string | null;
        };
        deployedFingerprint = buildInfo.fingerprint ?? null;
      }

      const remoteFingerprint = deployedFingerprint ?? data.client?.fingerprint ?? null;
      if (remoteFingerprint && remoteFingerprint !== localFingerprintRef.current) {
        handlersRef.current.onClientMismatch();
      } else if (
        deployedBuildResponse.ok &&
        deployedFingerprint &&
        deployedFingerprint === localFingerprintRef.current
      ) {
        handlersRef.current.onClientMatch?.();
      }

      const coursesAt = data.catalog?.coursesLatestPublishedAt ?? null;
      const gamesAt = data.catalog?.gamesLatestImportedAt ?? null;
      const previousSeen = catalogSeenRef.current;

      if (previousSeen === null) {
        catalogSeenRef.current = { courses: coursesAt, games: gamesAt };
      } else {
        const coursesChanged = isCatalogTimestampNewer(
          coursesAt,
          previousSeen.courses,
        );
        const gamesChanged = isCatalogTimestampNewer(gamesAt, previousSeen.games);

        if ((coursesChanged || gamesChanged) && handlersRef.current.onCatalogChange) {
          handlersRef.current.onCatalogChange({
            courses: coursesChanged,
            games: gamesChanged,
          });
        }

        catalogSeenRef.current = {
          courses: coursesAt ?? previousSeen.courses,
          games: gamesAt ?? previousSeen.games,
        };
      }
    } catch {
      // Ignore transient network errors during background checks.
    } finally {
      checkingRef.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    void check();
  }, [check, enabled, location.pathname]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void check();
    }, POLL_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    };

    const onFocus = () => {
      void check();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [check, enabled]);
}
